<template>
    <v-container class="py-7">
        <v-row>
            <v-col :cols="12">
                <div class="title-container">
                    <div class="title-block">
                        <span class="page-title">{{ page.title }}</span>
                        <v-breadcrumbs class="app-breadcrumb" divider="›" :items="dataset.breadcrumbItems"></v-breadcrumbs>
                    </div>
                    <div class="button-group">
                        <v-btn id="btnBack" type="button" elevation="0" color="primary" outlined
                            to="/divergencias">Voltar</v-btn>
                        <v-btn type="button" elevation="0" color="primary" outlined :loading="zipping"
                            :disabled="!dataset.data.id" @click="downloadDocuments">
                            <v-icon left small>mdi-folder-zip-outline</v-icon>Documentos (.zip)
                        </v-btn>
                    </div>
                </div>
            </v-col>
        </v-row>

        <v-row v-if="dataset.data && dataset.data.user">
            <!-- Coluna principal: o que está sendo analisado -->
            <v-col cols="12" lg="8">
                <v-card class="card-form-outlined event-section">
                    <div class="event-section__title">Credor</div>
                    <div class="info-grid">
                        <div class="info-item info-item--wide">
                            <span class="info-item__label">Nome</span>
                            <span class="info-item__value text-capitalize">{{ dataset.data.user.name }}</span>
                        </div>
                        <div class="info-item">
                            <span class="info-item__label">CPF/CNPJ</span>
                            <span class="info-item__value">{{ formatRegistration(dataset.data.user.registration) }}</span>
                        </div>
                        <div class="info-item">
                            <span class="info-item__label">Tipo de credor</span>
                            <span class="info-item__value">{{ creditorTypeName(dataset.data.user.creditor_type) }}</span>
                        </div>
                        <div class="info-item info-item--wide">
                            <span class="info-item__label">E-mail</span>
                            <span class="info-item__value">{{ dataset.data.user.email }}</span>
                        </div>
                    </div>
                    <a v-if="dataset.data.user.document_url" class="doc-link mt-4" :href="dataset.data.user.document_url"
                        target="_blank" rel="noopener">
                        <v-icon small color="primary">mdi-paperclip</v-icon> Documento de identificação do credor
                    </a>
                </v-card>

                <v-card v-if="dataset.data.representatives && dataset.data.representatives.length"
                    class="card-form-outlined event-section">
                    <div class="event-section__title">Representação</div>
                    <v-row>
                        <v-col v-for="representative in dataset.data.representatives" :key="representative.id" cols="12" md="6">
                            <div class="representative-card">
                                <span class="info-item__label">{{ representativeKindName(representative.kind) }}</span>
                                <p class="mb-1 mt-1"><strong>{{ representative.name }}</strong></p>
                                <p class="mb-1" v-if="representative.registration">CPF: {{ representative.registration }}</p>
                                <p class="mb-1" v-if="representative.oab">OAB: {{ representative.oab }}</p>
                                <p class="mb-2">{{ representative.email }} · {{ representative.phone_number }}</p>
                                <template v-for="docItem in representative.docs">
                                    <a v-if="docItem.doc" :key="docItem.id" class="doc-link" :href="docItem.doc.file_url"
                                        target="_blank" rel="noopener">
                                        <v-icon small color="primary">mdi-paperclip</v-icon> {{ docItem.doc.name }}
                                    </a>
                                </template>
                            </div>
                        </v-col>
                    </v-row>
                </v-card>

                <v-card class="card-form-outlined event-section">
                    <div class="event-section__title">Créditos pedidos</div>

                    <div v-for="(eventItem, eventIndex) in dataset.data.items" :key="eventIndex" class="credit-item">
                        <div class="credit-item__header">
                            <div>
                                <span class="info-item__label">Valor pedido</span>
                                <div class="credit-item__amount">
                                    {{ eventItem.currency | enum(dataset.currencyTypes, 'id', 'currencyFormat') }}
                                    {{ eventItem.amount | currency }}
                                </div>
                            </div>
                            <div v-if="acceptedAmount(eventItem) !== null" class="credit-item__accepted">
                                <span class="info-item__label">Valor aceito</span>
                                <div class="credit-item__amount credit-item__amount--accepted">
                                    {{ eventItem.currency | enum(dataset.currencyTypes, 'id', 'currencyFormat') }}
                                    {{ acceptedAmount(eventItem) | currency }}
                                </div>
                            </div>
                            <v-spacer></v-spacer>
                            <v-btn v-if="dataset.data.status === 0" small outlined color="primary" elevation="0"
                                @click="partialAcceptModal(eventItem)">Informar valor aceito</v-btn>
                        </div>

                        <div class="info-grid">
                            <div class="info-item">
                                <span class="info-item__label">Origem</span>
                                <span class="info-item__value">{{ eventItem.origin || '—' }}</span>
                            </div>
                            <div class="info-item">
                                <span class="info-item__label">Classe</span>
                                <span class="info-item__value">{{ classLabel(eventItem.type) }}</span>
                            </div>
                            <div class="info-item">
                                <span class="info-item__label">Moeda</span>
                                <span class="info-item__value">{{ eventItem.currency | enum(dataset.currencyTypes, 'id', 'name') }}</span>
                            </div>
                            <div v-if="eventItem.analyzed_type" class="info-item">
                                <span class="info-item__label">Classe analisada</span>
                                <span class="info-item__value">{{ classLabel(eventItem.analyzed_type) }}</span>
                            </div>
                            <div v-if="eventItem.analyzed_currency" class="info-item">
                                <span class="info-item__label">Moeda analisada</span>
                                <span class="info-item__value">{{ eventItem.analyzed_currency | enum(dataset.currencyTypes, 'id', 'name') }}</span>
                            </div>
                            <div class="info-item info-item--wide">
                                <span class="info-item__label">Observações</span>
                                <span class="info-item__value">{{ eventItem.observation || '—' }}</span>
                            </div>
                        </div>

                        <div v-if="eventItem.docs && eventItem.docs.length" class="mt-3">
                            <span class="info-item__label">Documentos ({{ eventItem.docs.length }})</span>
                            <div class="mt-1">
                                <template v-for="(docItem, docIndex) in eventItem.docs">
                                    <v-chip v-if="docItem.doc" :key="docIndex" small outlined color="primary" class="mr-2 mb-2"
                                        :href="docItem.doc.file_url" target="_blank">
                                        <v-icon left small>mdi-paperclip</v-icon>{{ docItem.doc.name || 'Documento' }}
                                    </v-chip>
                                </template>
                            </div>
                        </div>
                    </div>
                </v-card>

                <v-card class="card-form-outlined event-section">
                    <div class="event-section__title">Contraditório da devedora</div>

                    <div v-if="!dataset.data.contradictories || !dataset.data.contradictories.length" class="empty-text">
                        Nenhum contraditório recebido.
                    </div>

                    <div v-for="c in dataset.data.contradictories || []" :key="c.id" class="contradictory-item">
                        <div class="d-flex flex-wrap align-center">
                            <v-chip small text-color="white" class="mr-4" :color="contradictoryStatus(c.status).color">
                                {{ contradictoryStatus(c.status).name }}
                            </v-chip>
                            <span v-if="c.amount !== null" class="mr-4"><strong>Valor reconhecido:</strong> R$ {{ c.amount | currency }}</span>
                            <v-spacer></v-spacer>
                            <span class="caption">Enviado em {{ c.created_at | defaultDate('DD/MM/YYYY HH:mm') }}{{ c.creator ? ` por ${c.creator.name}` : '' }}</span>
                        </div>
                        <p v-if="c.justification" class="mt-3 mb-0" style="white-space: pre-line">{{ c.justification }}</p>
                        <div v-if="c.docs && c.docs.length" class="mt-3">
                            <v-chip v-for="doc in c.docs" :key="doc.id" small class="mr-2 mb-2" outlined color="primary"
                                :href="doc.url" target="_blank">
                                <v-icon left small>mdi-paperclip</v-icon>{{ doc.name }}
                            </v-chip>
                        </div>
                    </div>
                </v-card>

                <v-card class="card-form-outlined event-section">
                    <div class="event-section__title">Motivo da decisão</div>
                    <p class="event-section__hint">O credor vê o texto curto na lista de pedidos e o texto longo no detalhe.</p>

                    <div class="d-flex align-center justify-space-between mb-2">
                        <span class="font-weight-bold">Texto curto</span>
                        <v-btn v-show="dataset.data.status == 0" small text color="primary" @click="openDialogDynamicText(true)">
                            <v-icon left small>mdi-text-short</v-icon> Textos predefinidos
                        </v-btn>
                    </div>
                    <tiptap-vuetify class="custom-editor-short" :disabled="dataset.data.status !== 0"
                        v-model="dataset.data.smallReason" :rules="[$rules.required]" :extensions="extensions"
                        hide-details="auto" />

                    <div class="d-flex align-center justify-space-between mt-6 mb-2">
                        <span class="font-weight-bold">Texto longo</span>
                        <v-btn v-show="dataset.data.status == 0" small text color="primary" @click="openDialogDynamicText(false)">
                            <v-icon left small>mdi-text-long</v-icon> Textos predefinidos
                        </v-btn>
                    </div>
                    <tiptap-vuetify class="custom-editor-long" :disabled="dataset.data.status !== 0"
                        v-model="dataset.data.reason" :rules="[$rules.required]" :extensions="extensions"
                        hide-details="auto" />
                </v-card>
            </v-col>

            <!-- Painel lateral: situação e o que fazer -->
            <v-col cols="12" lg="4">
                <div class="event-aside">
                    <v-card class="card-form-outlined event-section">
                        <div class="event-section__title">Situação</div>
                        <v-chip label text-color="white" :color="statusInfo.color" class="mb-3">{{ statusInfo.name }}</v-chip>

                        <div class="aside-line">
                            <span>Tipo</span>
                            <strong>{{ dataset.data.type == 2 ? 'Divergência' : 'Habilitação' }}</strong>
                        </div>
                        <div class="aside-line">
                            <span>Caso</span>
                            <strong>{{ dataset.data.client ? dataset.data.client.name : '—' }}</strong>
                        </div>
                        <div class="aside-line">
                            <span>Solicitado em</span>
                            <strong>{{ dataset.data.created_at | defaultDate('DD/MM/YYYY HH:mm') }}</strong>
                        </div>
                        <div v-if="dataset.data.status !== 0" class="aside-line">
                            <span>Analisado por</span>
                            <strong class="text-capitalize">{{ dataset.data.admin ? dataset.data.admin.name : '—' }}</strong>
                        </div>

                        <template v-if="dataset.data.status == 0">
                            <v-divider class="my-4"></v-divider>
                            <p class="event-section__hint mb-3">Preencha o motivo antes de decidir. Para aprovar parcialmente, informe o valor aceito em cada crédito.</p>
                            <v-btn block elevation="0" color="success" class="mb-2" :loading="dataForm.addLoading"
                                @click="updateStatus('approve')">Aprovar</v-btn>
                            <v-btn block elevation="0" color="primary" outlined class="mb-2" :loading="dataForm.addLoading"
                                @click="updateStatus('partial')">Aprovar parcialmente</v-btn>
                            <v-btn block elevation="0" color="error" outlined :loading="dataForm.addLoading"
                                @click="updateStatus('reject')">Rejeitar</v-btn>
                        </template>
                        <template v-else>
                            <v-divider class="my-4"></v-divider>
                            <v-btn block text color="primary" @click="statusReset()">
                                <v-icon left small>mdi-restore</v-icon> Voltar para pendente
                            </v-btn>
                        </template>
                    </v-card>

                    <v-card class="card-form-outlined event-section">
                        <div class="event-section__title">Prazo do contraditório</div>
                        <p class="event-section__hint">A devedora envia o contraditório pelo Painel da Devedora até esta data.</p>
                        <v-text-field dense outlined type="date" label="Data limite" v-model="contradictory.date"
                            hide-details="auto" class="mb-3" />
                        <v-btn block elevation="0" color="primary" outlined :loading="contradictory.saving"
                            @click="saveContradictoryDate">Salvar prazo</v-btn>
                    </v-card>

                    <v-card class="card-form-outlined event-section">
                        <div class="event-section__title">Relatório de análise</div>
                        <p class="event-section__hint">PDF que o credor vê na Área do Credor depois da análise.</p>
                        <div v-if="dataset.data.report_url" class="d-flex align-center mb-3">
                            <a class="doc-link" :href="dataset.data.report_url" target="_blank" rel="noopener">
                                <v-icon small color="error">mdi-file-pdf-box</v-icon>
                                {{ dataset.data.report_name || 'Relatório de análise.pdf' }}
                            </a>
                            <v-spacer></v-spacer>
                            <v-btn small text color="error" :loading="report.removing" @click="removeReport">Remover</v-btn>
                        </div>
                        <v-file-input v-model="report.file" dense accept="application/pdf" prepend-icon=""
                            prepend-inner-icon="mdi-paperclip" outlined show-size hide-details="auto" class="mb-3"
                            :label="dataset.data.report_url ? 'Substituir relatório (PDF)' : 'Relatório (PDF)'"
                            :clearable="true">
                        </v-file-input>
                        <v-btn block elevation="0" color="primary" :disabled="!report.file" :loading="report.uploading"
                            @click="uploadReport">Enviar relatório</v-btn>
                    </v-card>
                </div>
            </v-col>
        </v-row>

        <v-dialog v-model="dialogDynamicText" :width="900">
            <v-card :max-height="700">
                <v-card-text>
                    <v-row>
                        <v-col>
                            <div class="font-weight-bold mt-6 title-page">Textos Automáticos</div>
                            <p class="mt-2">
                                Selecione um texto abaixo para adicionar no motivo
                            </p>
                        </v-col>
                    </v-row>
                    <v-row>
                        <v-col>
                            <div>
                                <v-list-item two-line v-for="(item, index) in dataset.dynamicTexts" :key="index"
                                    style="cursor: pointer" @click="selectDynamicText(item)">
                                    <v-list-item-content>
                                        <v-list-item-title class="font-weight-bold">{{ item.name }}</v-list-item-title>
                                        <v-list-item-subtitle>
                                            <div v-html="dynamicTextSmallType ? item.small_description : item.description">
                                            </div>
                                            <v-divider></v-divider>
                                        </v-list-item-subtitle>
                                    </v-list-item-content>
                                </v-list-item>
                            </div>
                            <!-- <v-card class="rounded-lg mb-10" v-for="(item, index) in dataset.dynamicTexts" :key="index"
                                elevation="2">
                                <v-card-title>
                                    {{ item.name }}
                                </v-card-title>
                                <v-card-text>
                                    <div v-html="dynamicTextSmallType ? item.small_description : item.description"></div>
                                </v-card-text>
                            </v-card> -->
                        </v-col>
                    </v-row>
                </v-card-text>
                <v-card-text>
                    <v-row>
                        <v-col>
                            <v-btn type="button" elevation="0" color="error"
                                @click="dialogDynamicText = false;">Cancelar</v-btn>
                        </v-col>
                    </v-row>
                </v-card-text>
            </v-card>
        </v-dialog>


        <v-dialog v-model="dialogPartialAccept" :width="700">
            <v-card :max-height="500" v-if="dataset.selectedItem">
                <v-card-text>
                    <v-row>
                        <v-col>
                            <div class="font-weight-bold mt-6 title-page">Aceitar parcialmente</div>
                        </v-col>
                    </v-row>
                    <v-row>
                        <v-col cols="12" sm="6">
                            <p>
                                <span class="section-info-title ">Valor solicitado credor</span>
                                <br />
                                <span class="text-uppercase text-h6">{{
                                    dataset.selectedItem.currency
                                    | enum(
                                        dataset.currencyTypes,
                                        'id',
                                        'currencyFormat'
                                    )
                                }}
                                    {{ dataset.selectedItem.amount | currency }}
                                </span>
                            </p>

                        </v-col>
                    </v-row>

                    <v-row>
                        <v-col cols="12" sm="6">
                            <span class="section-info-title">Digite valor aceito</span>
                            <br />
                            <br />
                            <v-currency-field label="Valor" dense v-model="dataset.selectedItem.partialAmount"
                                :allowNegative=false hide-details="auto" outlined class="mt-n3">
                            </v-currency-field>
                        </v-col>
                    </v-row>
                </v-card-text>

                <v-divider></v-divider>
                <v-card-actions>
                    <div class="mr-10 ml-2">
                        <v-tooltip bottom>
                            <template #activator="{ on }">
                                <v-btn v-on="on" @click="savePartialValue()" color="primary" dark elevation="2"
                                    :loading="dataset.loading">
                                    OK
                                </v-btn>
                            </template>
                            <span>Salvar valor parcial</span>
                        </v-tooltip>
                    </div>
                    <div>
                        <v-tooltip bottom>
                            <template #activator="{ on }">
                                <v-btn v-on="on" color="grey" text
                                    @click.native="dialogPartialAccept = false">Cancelar</v-btn>
                            </template>
                            <span>Fechar</span>
                        </v-tooltip>
                    </div>
                </v-card-actions>

            </v-card>
        </v-dialog>

        <v-dialog v-model="dialogViewDoc" max-width="60%" @keydown.esc="cancel">
            <v-card>
                <v-img :src="selectedDoc" alt="" contain />
            </v-card>
        </v-dialog>
    </v-container>
</template>

<script>
import states from "@/common/data/states.data"
import string from "@/common/util/string"

import userTypes from "@/common/data/user-types.data"
import currencyTypes from "@/common/data/currency-types.data"

import axios from "axios"

import {
    TiptapVuetify, Heading, Bold, Italic, Strike, Underline, Code, Paragraph, BulletList, OrderedList, ListItem, Link, Blockquote, HardBreak, HorizontalRule, History, Table, TableCell, TableHeader,
    TableRow, TodoList, TodoItem
} from 'tiptap-vuetify'


export default {
    name: "UserDetails",

    components: { 'tiptap-vuetify': TiptapVuetify },

    data: () => ({
        zipping: false,
        page: {
            isNew: false,
            title: "",
        },
        dataset: {
            userTypes: userTypes,
            currencyTypes: currencyTypes,
            breadcrumbItems: [],
            states: states,
            selectedItem: null,
            data: {
                smallReason: "",
                reason: "",
                partialAmount: null,
            },
            partialItems: [],
            dynamicTexts: [],
            events: []
        },
        report: {
            file: null,
            uploading: false,
            removing: false,
        },
        contradictory: {
            date: "",
            saving: false,
        },
        dialogDynamicText: false,
        dialogPartialAccept: false,
        dynamicTextSmallType: false,
        selectedDoc: null,
        dialogViewDoc: false,
        dataForm: {
            validForm: true,
            addLoading: false,
            updateLoading: false,
            removeLoading: false,
            showPassword: false,
            showPasswordConfirmation: false,
        },
        extensions: [
            History,
            Blockquote,
            Link,
            Underline,
            Strike,
            Italic,
            ListItem,
            BulletList,
            OrderedList,
            [Heading, {
                options: {
                    levels: [1, 2, 3]
                }
            }],
            Bold,
            Code,
            HorizontalRule,
            Paragraph,
            HardBreak,
            [Table, {
                options: {
                    resizable: true
                }
            }],
            TableCell,
            TableHeader,
            TableRow,
        ],
    }),

    created() {
        this.loadData()
    },

    computed: {
        statusInfo() {
            return {
                0: { name: 'Pendente de análise', color: 'warning' },
                1: { name: 'Aprovada', color: 'success' },
                2: { name: 'Rejeitada', color: 'error' },
                3: { name: 'Aprovada parcialmente', color: 'info' },
            }[this.dataset.data.status] || { name: '—', color: 'grey' };
        },
    },

    methods: {
        // Classe do crédito; na falência o id 5 é "Extraconcursais".
        classLabel(id) {
            const bankruptcy = this.dataset.data.client && Number(this.dataset.data.client.type) === 2
            return {
                1: 'Classe I - Trabalhista',
                2: 'Classe II - Garantia Real',
                3: 'Classe III - Quirografários',
                4: 'Classe IV - ME/EPP',
                5: bankruptcy ? 'Extraconcursais' : 'Extraconcursal/Pago',
                6: 'Tributário',
            }[Number(id)] || '—'
        },

        async downloadDocuments() {
            this.zipping = true
            try {
                const result = await axios.get(`/api/admin/user-events/${this.dataset.data.id}/documents`, { responseType: 'blob' })
                const link = document.createElement('a')
                link.href = URL.createObjectURL(result.data)
                link.download = `hab-div-${this.dataset.data.id}-documentos.zip`
                document.body.appendChild(link)
                link.click()
                link.remove()
                setTimeout(() => URL.revokeObjectURL(link.href), 1000)
            } catch (error) {
                if (error.response && error.response.data instanceof Blob) {
                    try { error.response.data = JSON.parse(await error.response.data.text()) } catch (e) { /* mantém */ }
                }
                this.$dialog.notify.error(this.$codes(error) || 'Não foi possível gerar o .zip.', { position: 'top-right', timeout: 6000 })
            } finally {
                this.zipping = false
            }
        },
        goBack() {
            this.$router.replace("/divergencias");
        },

        openDialogDynamicText(small = false) {
            this.loadDynamicTexts()

            this.dynamicTextSmallType = small;
            this.dialogDynamicText = true;

        },

        openDoc(doc) {
            window.open(doc, '_blank');
            // this.selectedDoc = doc;
            // this.dialogViewDoc = true;
        },

        truncateText(text, maxLength) {
            return text.length > maxLength ? `${text.slice(0, maxLength)}...` : text;
        },

        getMessageByTypeEvent(item) {
            let result = '';

            switch (item.type) {
                case 'login-account':
                    result = `Usuário acessou o sistema no dia ${this.$moment(item.created_at).format('DD/MM/YYYY HH:mm')}`;
                    break;

                case 'accept-value':
                    result = `Usuário aceitou o crédito no valor de R$ ${this.dataset.data.requested_value} no dia ${this.$moment(item.created_at).format('DD/MM/YYYY HH:mm')}`;
                    break;

                case 'verify-account':
                    result = `Usuário verificou a conta no dia ${this.$moment(item.created_at).format('DD/MM/YYYY HH:mm')}`;
                    break;

                case 'register-user':
                    result = `Usuário criou a conta no dia ${this.$moment(item.created_at).format('DD/MM/YYYY HH:mm')}`;
                    break;

                case 'create-event':
                    result = `Usuário registrou uma divergência no dia ${this.$moment(item.created_at).format('DD/MM/YYYY HH:mm')}`;
                    break;

                default:
                    break;
            }

            return result;
        },

        getTitleByTypeEvent(item) {
            let result = '';

            switch (item.type) {
                case 'login-account':
                    result = `acesso`;
                    break;

                case 'accept-value':
                    result = `CRÉDITO`;
                    break;

                case 'verify-account':
                    result = `verificação`;
                    break;

                case 'register-user':
                    result = `cadastro`;
                    break;

                case 'create-event':
                    result = `divergência`;
                    break;

                default:
                    break;
            }

            return result;
        },

        async loadData() {
            await this.load();

            this.setPage();
            this.listBreadCrumbItems();
        },

        setPage() {
            this.page.isNew = this.$route.params.id === "novo";
            this.page.title = `Detalhes da ${this.dataset.data.user.uuid_ref ? 'divergência' : 'habilitação'}`;
        },

        listBreadCrumbItems() {
            this.dataset.breadcrumbItems = [
                {
                    text: "Início",
                    disabled: false,
                    to: "/home",
                    exact: true,
                },
            ];

            this.dataset.breadcrumbItems.push({
                text: "Divergência",
                disabled: false,
                to: "/divergencias",
                exact: true,
            })

            this.dataset.breadcrumbItems.push({
                text: `Detalhes da ${this.dataset.data.user.uuid_ref ? 'divergência' : 'habilitação'}`,
                disabled: true,
                exact: true,
            });
        },

        selectDynamicText(item) {
            if (this.dynamicTextSmallType) {
                this.dataset.data.smallReason += item.small_description
            } else {
                this.dataset.data.reason += item.description
            }

            this.dialogDynamicText = false;
        },

        async updateStatus(approve = 'reject') {

            if (approve === 'partial' && (!this.dataset.partialItems.length)) {
                this.$dialog.notify.error("Informe os novos valores para continuar!", { position: "top-right", timeout: 2000 });
                return;
            }

            if (!this.dataset.data.smallReason || !this.dataset.data.reason) {
                this.$dialog.notify.error("Motivo é obrigatório", { position: "top-right", timeout: 2000 });
                return;
            }

            const proceedAction = await this.$dialog.confirm({
                text: `Deseja ${approve === 'approve' ? 'aprovar' : (approve === 'reject' ? 'rejeitar' : 'aprovar parcialmente')} a divergência?`,
                title: `${approve === 'approve' ? 'Aprovar' : (approve === 'reject' ? 'Rejeitar' : 'Aprovar parcialmente')} divergência`,
                actions: {
                    false: 'Cancelar',
                    true: approve === 'approve' ? 'Aprovar' : (approve === 'reject' ? 'Rejeitar' : 'Aprovar parcialmente')
                }
            })

            if (!proceedAction) return;

            this.dataForm.addLoading = true;

            const data = {
                smallReason: this.dataset.data.smallReason,
                reason: this.dataset.data.reason,
                status: approve === 'approve' ? 1 : (approve === 'reject' ? 2 : 3)
            }

            if (approve === 'partial') {
                data.eventItems = this.dataset.partialItems;
            }

            try {
                const response = await axios.patch(`/api/admin/user-events/action/${this.$route.params.id}`, data);

                this.$dialog.notify.info(`Divergência ${approve === 'approve' ? 'aprovada' :
                    (approve === 'reject' ? 'rejeitada' : 'aprovada parcialmente')} com sucesso!`, { position: "top-right", timeout: 2000 });

                this.goBack();

            } catch (error) {
                this.$dialog.notify.error(this.$codes(error), {
                    position: 'top-right',
                    timeout: 2000
                })
            } finally {
                this.dataForm.addLoading = false;
            }
        },

        // Valor aceito: o já gravado (aprovação parcial) ou o informado agora, antes de decidir
        acceptedAmount(item) {
            const pending = this.dataset.partialItems.find((x) => x.id == item.id);
            if (pending) return pending.amount;
            if (this.dataset.data.status === 3 && item.partial_amount !== null && item.partial_amount !== undefined) return item.partial_amount;
            return null;
        },

        formatRegistration(value) {
            const digits = String(value || '').replace(/\D/g, '');
            if (digits.length === 11) return digits.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4');
            if (digits.length === 14) return digits.replace(/(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})/, '$1.$2.$3/$4-$5');
            return value || '—';
        },

        creditorTypeName(type) {
            return { 1: 'Pessoa física', 2: 'Pessoa jurídica', 3: 'Espólio (inventariante)', 4: 'Herdeiro' }[type] || 'Não informado';
        },

        representativeKindName(kind) {
            return { 1: 'Advogado', 2: 'Representante legal', 3: 'Inventariante' }[kind] || 'Representante';
        },

        async uploadReport() {
            if (!this.report.file) return;

            if (this.report.file.type !== 'application/pdf') {
                this.$dialog.notify.error("Envie o relatório em PDF.", { position: "top-right", timeout: 3000 });
                return;
            }

            if (this.report.file.size > 20 * 1024 * 1024) {
                this.$dialog.notify.error("O relatório não pode ter mais de 20 MB.", { position: "top-right", timeout: 3000 });
                return;
            }

            const formData = new FormData();
            formData.append('file', this.report.file);

            this.report.uploading = true;

            try {
                await axios.post(`/api/admin/user-events/${this.$route.params.id}/report`, formData);

                this.$dialog.notify.info("Relatório enviado com sucesso!", { position: "top-right", timeout: 2000 });

                this.report.file = null;
                await this.reloadReport();
            } catch (error) {
                this.$dialog.notify.error(this.$codes(error), { position: 'top-right', timeout: 3000 })
            } finally {
                this.report.uploading = false;
            }
        },

        async removeReport() {
            const proceedAction = await this.$dialog.confirm({
                text: 'Deseja remover o relatório de análise? O credor deixará de vê-lo.',
                title: 'Remover relatório',
                actions: {
                    false: 'Cancelar',
                    true: 'Remover'
                }
            })

            if (!proceedAction) return;

            this.report.removing = true;

            try {
                await axios.delete(`/api/admin/user-events/${this.$route.params.id}/report`);

                this.$dialog.notify.info("Relatório removido.", { position: "top-right", timeout: 2000 });

                await this.reloadReport();
            } catch (error) {
                this.$dialog.notify.error(this.$codes(error), { position: 'top-right', timeout: 3000 })
            } finally {
                this.report.removing = false;
            }
        },

        // Atualiza só o relatório, sem descartar motivos ou valores parciais ainda não salvos.
        async reloadReport() {
            const result = await axios.get(`/api/admin/user-events/find/${this.$route.params.id}`);
            const data = result.data.content;

            this.$set(this.dataset.data, 'report_url', data.report_url);
            this.$set(this.dataset.data, 'report_name', data.report_name);
        },

        contradictoryStatus(status) {
            return {
                1: { name: "De acordo", color: "green" },
                2: { name: "Em desacordo", color: "red" },
                3: { name: "De acordo parcialmente", color: "orange" },
            }[status] || { name: "—", color: "grey" };
        },

        async saveContradictoryDate() {
            this.contradictory.saving = true;

            try {
                await axios.patch(`/api/admin/user-events/${this.$route.params.id}/contradictory-date`, {
                    contradictory_date: this.contradictory.date || null,
                });

                this.$dialog.notify.info(this.contradictory.date ? "Prazo salvo." : "Prazo removido.", { position: "top-right", timeout: 2000 });
            } catch (error) {
                this.$dialog.notify.error(this.$codes(error), { position: "top-right", timeout: 5000 });
            } finally {
                this.contradictory.saving = false;
            }
        },

        async load() {

            try {
                const result = await axios.get(`/api/admin/user-events/find/${this.$route.params.id}`);
               
                const data = result.data.content;

                this.dataset.data = data;
                this.contradictory.date = data.contradictory_date ? String(data.contradictory_date).substring(0, 10) : "";

                this.dataset.data.smallReason = data.small_reason || ""
                this.dataset.data.reason = data.reason || ""

            } catch (error) {
                this.$dialog.notify.error(this.$codes(error), {
                    position: 'top-right',
                    timeout: 2000
                })
            }
        },

        async loadDynamicTexts() {

            try {
                const result = await axios.get(`/api/admin/dynamic-texts`, { params: { start: 1, limit: 100000, search: {} } });

                this.dataset.dynamicTexts = result.data.content;

            } catch (error) {
                this.$dialog.notify.error(this.$codes(error), {
                    position: 'top-right',
                    timeout: 2000
                })
            }
        },

        buildPayload() {
            const data = {
                name: this.dataset.data.name,
                email: this.dataset.data.email,
                description: this.dataset.data.description,
                client: this.dataset.data.client,
                unit: this.dataset.data.unit,
                associations: this.normalizeAssociations(),
                credential: {
                    username: this.dataset.data.email,
                    password: this.dataset.data.password,
                },
                roles: this.dataset.data.roles

            }

            return data;
        },

        async statusReset(id) {

            const proceedAction = await this.$dialog.confirm({
                text: 'Tem certeza que deseja restaurar status do credor?',
                title: 'Restaurar status credor',
                actions: {
                    false: 'Cancelar',
                    true: 'Restaurar'
                }
            })

            if (!proceedAction) return;

            try {
                const response = await axios.patch(`/api/admin/user-events/action/${this.$route.params.id}`, { status: 0 });

                this.$dialog.notify.info('Status do credor restaurado com sucesso!', { position: "top-right", timeout: 2000 });

                this.goBack()

            } catch (error) {
                console.log("erro: ", error)
                this.$dialog.notify.error(this.$codes(error), {
                    position: 'top-right',
                    timeout: 2000
                })
            }
        },

        partialAcceptModal(item) {
            this.dataset.selectedItem = item;
            this.dialogPartialAccept = true;
        },

        savePartialValue() {

            if (!this.dataset.selectedItem.partialAmount) {

                this.$dialog.notify.error('Valor digitado é inválido!', { position: "top-right", timeout: 2000 });

                return;
            }

            const index = this.dataset.partialItems.findIndex((x) => x.id == this.dataset.selectedItem.id);

            if (index == -1) {
                this.dataset.partialItems.push({ id: this.dataset.selectedItem.id, amount: this.dataset.selectedItem.partialAmount })
            } else {
                this.dataset.partialItems[index] = { id: this.dataset.selectedItem.id, amount: this.dataset.selectedItem.partialAmount };
            }

            this.dialogPartialAccept = false;

        },

    },

};

</script>
  
<style lang="scss">
.event-section {
    padding: 20px 24px;
    margin-bottom: 0;
}

.event-section__title {
    margin-bottom: 14px;
    font-size: 16px;
    font-weight: 700;
    color: var(--v-primary-base);
}

.event-section__hint {
    margin-top: -8px;
    text-align: left;
    font-size: 13px;
    color: var(--v-muted-base);
}

// Painel lateral acompanha a rolagem; se for mais alto que a tela, rola por dentro
.event-aside {
    position: sticky;
    top: 84px;
    max-height: calc(100vh - 100px);
    overflow-y: auto;
    padding-bottom: 4px;
    scrollbar-width: thin;
}

.info-grid {
    display: grid;
    grid-template-columns: repeat(3, minmax(0, 1fr));
    gap: 14px 24px;
}

.info-item {
    display: flex;
    flex-direction: column;
    min-width: 0;
}

.info-item--wide {
    grid-column: span 3;
}

.info-item__label {
    font-size: 12px;
    color: var(--v-muted-base);
}

.info-item__value {
    font-size: 15px;
    color: var(--v-ink-base);
    overflow-wrap: anywhere;
}

.doc-link {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    font-size: 14px;
    text-decoration: none;
}

.credit-item {
    padding: 16px 0;
    border-top: 1px solid var(--v-lightGray-base);

    &:first-of-type {
        border-top: 0;
        padding-top: 0;
    }
}

.credit-item__header {
    display: flex;
    align-items: center;
    gap: 32px;
    margin-bottom: 14px;
}

.credit-item__amount {
    font-size: 22px;
    font-weight: 700;
    color: var(--v-primary-base);
}

.credit-item__amount--accepted {
    color: var(--v-copper-base);
}

.contradictory-item {
    padding: 14px 0;
    border-top: 1px solid var(--v-lightGray-base);

    &:first-of-type {
        border-top: 0;
        padding-top: 0;
    }
}

.empty-text {
    color: var(--v-muted-base);
}

.aside-line {
    display: flex;
    justify-content: space-between;
    gap: 16px;
    padding: 6px 0;
    font-size: 14px;

    span {
        color: var(--v-muted-base);
    }

    strong {
        text-align: right;
        color: var(--v-ink-base);
    }
}

@media (max-width: 960px) {
    .info-grid {
        grid-template-columns: 1fr;
    }

    .info-item--wide {
        grid-column: span 1;
    }

    .event-aside {
        position: static;
    }
}

.representative-card {
    height: 100%;
    padding: 14px 16px;
    border: 1px solid #e0e0e0;
    border-radius: 8px;
}

.access-container {
    border: 1px solid var(--v-gray-base) !important;
}

.custom-chip {
    background: var(--v-white-base) !important;
}

.title-page {
    color: var(--v-primary-base);
    // font-family: Montserrat;
    font-size: 40px;
    font-style: normal;
    font-weight: 700;
    line-height: 38px;
}

.section-info-title {
    color: var(--v-primary-base);
    // font-family: Montserrat;
    font-size: 16px;
    font-style: normal;
    font-weight: 700;
}

.section-info-value {
    // font-family: Montserrat;
    font-size: 14px;
    font-style: normal;
}

.custom-editor-short {
    .tiptap-vuetify-editor__content {
        min-height: 100px !important;
    }

    .ProseMirror {
        min-height: 100px !important;
    }
}

.custom-editor-long {
    .tiptap-vuetify-editor__content {
        min-height: 200px !important;
    }

    .ProseMirror {
        min-height: 200px !important;
    }
}

.icon-container {
    display: flex;
    align-items: center;
    border: 1px solid #ccc;
    padding: 10px;
    border-radius: 8px;
}
</style>